// Экран 4 — «Итог дня». День закрывается движением навыков, а не оценкой.
//
// Оценки за день и прогноза балла нет: он показывается раз в неделю, потому что
// ежедневная цель по результату повышает тревогу там, где стратегия ещё не
// освоена. Заголовок признаёт выполненную работу, но не оценивает ученика;
// просевший навык показан честно.

import { api, useQuery, type DayPlan } from "../api";
import { CoachNote } from "../ui/CoachNote";
import { completionCopy } from "../lib/completion-copy";
import { formatDate, plural } from "../lib/dates";
import { Moves } from "../ui/Moves";

export function DayEnd({
  learnerKey,
  date,
  plan,
  onToday,
  onOpenProgress,
}: {
  learnerKey: string;
  date: string;
  plan: DayPlan | null | undefined;
  onToday: () => void;
  onOpenProgress: () => void;
}) {
  const day = useQuery(api.progress.day, { learnerKey, date });
  const left = plan ? plan.assignments.filter((a) => !a.done).length : 0;
  const completed = plan != null && left === 0;
  const copy = completed ? completionCopy(learnerKey, date) : null;
  const coachShown = completed && day != null && day.total > 0;

  return (
    <div className="cols cols--solo">
      <div className="col">
        <div>
          <div className="eyebrow">{formatDate(date)}</div>
          <h1 className="h1">{copy?.acknowledgement ?? "Итог дня"}</h1>
          {/* «Отвечено», а не «решено»: ответ закрывает задачу и при ошибке,
              и «решено 3 из 3» после трёх неверных было бы похвалой вместо
              числа. «Решено» на «Прогрессе» значит «верно» — так и осталось. */}
          {day ? (
            <p className="lede">
              <span className="num">{day.minutes}</span>{" "}
              {plural(day.minutes, "минута", "минуты", "минут")} · отвечено{" "}
              <span className="num">{day.solved}</span> из <span className="num">{day.total}</span>
              {" · верно "}
              <span className="num">{day.correct}</span>
              {left > 0 ? ` · осталось ${left}` : ""}
            </p>
          ) : (
            <div className="skeleton" style={{ marginTop: 12, width: 240 }} />
          )}
        </div>

        {/*
          Тренер в конце дня: трудный день (верных меньше половины) — свои
          слова, обычный — свои. Только у закрытого набора: незаконченный день
          тренер не подытоживает (решение 30 октября 2026).
        */}
        {coachShown && day ? (
          <CoachNote
            situation={day.correct / day.total < 0.5 ? "hardDay" : "dayDone"}
            seedParts={[learnerKey, date]}
          />
        ) : null}

        {/* Мысль после занятия — только когда тренер молчит: два ободрения
            подряд говорят одно и то же (решение владельца 30 октября 2026). */}
        {copy && !coachShown ? (
          <div className="advice">
            <div>
              <p className="advice__state">Мысль после занятия</p>
              <p className="advice__text">{copy.reflection}</p>
            </div>
          </div>
        ) : null}

        <div className="card">
          <div className="card__label">Навыки за сегодня</div>
          {day ? (
            <Moves moves={day.moves} scope="day" />
          ) : (
            <div className="skeleton" style={{ marginTop: 16 }} />
          )}
        </div>

        {day && day.sagged.length > 0 ? (
          <div className="card">
            <div className="card__label">Просело</div>
            <div className="stack stack--tight" style={{ marginTop: 12 }}>
              {day.sagged.map((skill) => (
                <div key={skill.skillId} style={{ fontSize: 15, fontWeight: 500 }}>
                  {skill.name}
                </div>
              ))}
            </div>
            <div className="muted" style={{ marginTop: 12, fontSize: 14, lineHeight: 1.6 }}>
              Стоит первым в завтрашнем наборе — интервал сжат, повтор придёт раньше.
            </div>
          </div>
        ) : null}

        <div className="row row--actions" style={{ marginTop: 8 }}>
          <button className="btn btn--primary btn--big" onClick={onToday}>
            {completed ? "К сегодняшнему набору" : "Вернуться к набору"}
          </button>
          <button className="link link--quiet" onClick={onOpenProgress}>
            Прогресс по карте
          </button>
        </div>
      </div>
    </div>
  );
}
